import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const CustomerDashboard = () => {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchBookings();
  }, [navigate]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/bookings/my-bookings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#48bb78';
      case 'pending': return '#ed8936';
      case 'cancelled': return '#e53e3e';
      case 'used': return '#667eea';
      default: return '#a0aec0';
    }
  };

  const downloadTicket = (booking) => {
    const ticketData = `
TIKET JINI - E-TICKET
=====================
Booking ID: ${booking.bookingId}
Customer: ${booking.customerDetails.name}
Attraction: ${booking.ticketTypeId?.name}
Quantity: ${booking.quantity}
Amount: ₹${booking.totalAmount}
Visit Date: ${new Date(booking.visitDate).toLocaleDateString()}
Valid Till: ${new Date(booking.validTill).toLocaleString()}
Status: ${booking.status.toUpperCase()}

QR Code: ${booking.qrCode ? 'Available' : 'Not Generated'}
=====================
Thank you for choosing Tiket Jini!
    `;
    
    const blob = new Blob([ticketData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ticket-${booking.bookingId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⏳</div>
          <p style={{ fontSize: '1.2rem', color: '#4a5568' }}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem'
              }}>
                👤
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                  Welcome, {user?.name}!
                </h1>
                <p style={{ margin: '4px 0 0', opacity: 0.9 }}>
                  Manage your bookings and explore new experiences
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                🏠 Home
              </button>
              <button
                onClick={handleLogout}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 24px' }}>
        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '40px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #48bb78, #38a169)',
            color: 'white',
            padding: '24px',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎫</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {bookings.length}
            </div>
            <div style={{ opacity: 0.9 }}>Total Bookings</div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            padding: '24px',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {bookings.filter(b => b.status === 'confirmed').length}
            </div>
            <div style={{ opacity: 0.9 }}>Confirmed</div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #ff6b6b, #feca57)',
            color: 'white',
            padding: '24px',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>💰</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              ₹{bookings.reduce((sum, booking) => sum + booking.totalAmount, 0).toLocaleString()}
            </div>
            <div style={{ opacity: 0.9 }}>Total Spent</div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #4ecdc4, #44a08d)',
            color: 'white',
            padding: '24px',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎢</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
              {bookings.filter(b => b.status === 'used').length}
            </div>
            <div style={{ opacity: 0.9 }}>Experiences</div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '30px',
          background: 'white',
          padding: '8px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          {[
            { id: 'bookings', label: '🎫 My Bookings', icon: '🎫' },
            { id: 'profile', label: '👤 Profile', icon: '👤' },
            { id: 'preferences', label: '⚙️ Preferences', icon: '⚙️' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: activeTab === tab.id ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#4a5568',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'bookings' && (
          <div>
            {bookings.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '60px',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '24px' }}>🎫</div>
                <h3 style={{ color: '#2d3748', marginBottom: '16px' }}>No Bookings Yet</h3>
                <p style={{ color: '#718096', marginBottom: '32px' }}>
                  Start exploring amazing experiences and book your first ticket!
                </p>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  🎢 Explore Experiences
                </button>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                {bookings.map(booking => (
                  <div key={booking._id} style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr auto',
                    gap: '20px',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <h3 style={{ margin: 0, color: '#2d3748', fontSize: '1.25rem' }}>
                          {booking.ticketTypeId?.name}
                        </h3>
                        <span style={{
                          background: getStatusColor(booking.status),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {booking.status.toUpperCase()}
                        </span>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', fontSize: '14px', color: '#718096' }}>
                        <div>
                          <strong>Booking ID:</strong> {booking.bookingId}
                        </div>
                        <div>
                          <strong>Visit Date:</strong> {new Date(booking.visitDate).toLocaleDateString()}
                        </div>
                        <div>
                          <strong>Quantity:</strong> {booking.quantity}
                        </div>
                        <div>
                          <strong>Amount:</strong> ₹{booking.totalAmount}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', flexDirection: window.innerWidth <= 768 ? 'row' : 'column' }}>
                      <button
                        onClick={() => navigate(`/booking/${booking.bookingId}`)}
                        style={{
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => downloadTicket(booking)}
                        style={{
                          background: '#48bb78',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 24px', color: '#2d3748' }}>👤 Profile Information</h2>
            
            <div style={{ display: 'grid', gap: '20px', maxWidth: '500px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Name</label>
                <input
                  type="text"
                  value={user?.name || ''}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    background: '#f8fafc'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    background: '#f8fafc'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Role</label>
                <input
                  type="text"
                  value={user?.role?.replace('_', ' ').toUpperCase() || ''}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    background: '#f8fafc'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 24px', color: '#2d3748' }}>⚙️ Preferences</h2>
            
            <div style={{ display: 'grid', gap: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 16px', color: '#4a5568' }}>Notifications</h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked style={{ marginRight: '12px' }} />
                    <span>Email notifications for booking confirmations</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked style={{ marginRight: '12px' }} />
                    <span>SMS notifications for important updates</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginRight: '12px' }} />
                    <span>Promotional offers and discounts</span>
                  </label>
                </div>
              </div>
              
              <div>
                <h3 style={{ margin: '0 0 16px', color: '#4a5568' }}>Privacy</h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" defaultChecked style={{ marginRight: '12px' }} />
                    <span>Allow location-based recommendations</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginRight: '12px' }} />
                    <span>Share booking data for analytics</span>
                  </label>
                </div>
              </div>
              
              <button
                style={{
                  background: 'linear-gradient(45deg, #48bb78, #38a169)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: 'fit-content'
                }}
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;